import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  Box,
  Grid,
  CircularProgress,
  Tooltip,
  Paper,
  Alert,
} from "@mui/material";
import React from "react";
import ServerAppGrid from "../../../app/ServerAppGrid";
import {
  PricingType,
  ProductType,
} from "../../../../models/features/Integration/DmsSetup";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import {
  useGetSyncedProductsMutation,
  useSetPriceSyncingTypeMutation,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import { AppFilter } from "../../../../models/app/AppModels";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducer/rootReducer";
import { setPriceSynceType } from "../../../../redux/reducer/features/Auth/authSlice";
import InfoIcon from "@mui/icons-material/Info";

export interface NewPriceProps {
  isOpen: boolean;
  handleClose: any;
}

const GridWithNewPrices: React.FunctionComponent<NewPriceProps> = ({
  handleClose,
  isOpen,
}) => {
  const setupId = useSelector((state: AppState) => state.authReducer.setupId);
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);

  const dispatch = useDispatch();
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
  });
  const [priceType, setPriceType] = React.useState<PricingType>(
    PricingType.Price
  );
  const [products, setProducts] = React.useState<any[]>([]);
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [applyRuleConfirm, setApplyRuleConfirm] =
    React.useState<boolean>(false);

  const [productsMutation, productsMutationResult] =
    useGetSyncedProductsMutation();
  const [setPriceSyncingMutation, setPriceSyncingMutationResult] =
    useSetPriceSyncingTypeMutation();

  React.useEffect(() => {
    fetchData();
  }, [paging]);

  React.useEffect(() => {
    const key = dmsSetup?.pricing ?? "Price";
    const enumValue = PricingType[key as keyof typeof PricingType];
    if (enumValue !== undefined) {
      setPriceType(enumValue);
    }
  }, []);

  React.useEffect(() => {
    if (productsMutationResult.data) {
      setProducts(productsMutationResult.data.entityList);
      setProductsCount(productsMutationResult.data.totalRecords);
    }
  }, [productsMutationResult]);

  const handleCloseConfirmation = () => {
    setApplyRuleConfirm(false);
  };
  const fetchData = () => {
    productsMutation({
      setupId: setupId ?? "",
      paging: paging,
      productType: ProductType.Whoolgoods,
    });
  };

  const handleApplyPriceRule = () => {
    setPriceSyncingMutation({
      pricing: priceType,
      setupId: setupId ?? "",
    }).then((res: any) => {
      if (res?.data && res?.data?.status === 200) {
        dispatch(setPriceSynceType({ type: PricingType[priceType] }));
        setApplyRuleConfirm(false);
        handleClose();
      }
    });
  };
  const handlePriceTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    setPriceType(value as PricingType);
  };

  const handleGetSelectedPrice = (product: any) => {
    var selectedPrice = 0;
    switch (priceType as PricingType) {
      case PricingType.Price:
        selectedPrice = product.price;
        break;
      case PricingType.RetailPrice:
        selectedPrice = product?.aggregatePrices?.msrpListPriceLatestStock;
        break;
      case PricingType.UnitListPriceLargestStock:
        selectedPrice = product?.aggregatePrices?.unitListPriceLargestStock;
        break;
      case PricingType.UnitListPriceSmallestStock:
        selectedPrice = product?.aggregatePrices?.unitListPriceSmallestStock;
        break;
      case PricingType.UnitListPriceLatestStock:
        selectedPrice = product?.aggregatePrices?.unitListPriceLatestStock;
        break;
      case PricingType.MSRPListPriceLargestStock:
        selectedPrice = product?.aggregatePrices?.msrpListPriceLargestStock;
        break;
      case PricingType.MSRPListPriceSmallestStock:
        selectedPrice = product?.aggregatePrices?.msrpListPriceSmallestStock;
        break;
      case PricingType.MSRPListPriceLatestStock:
        selectedPrice = product?.aggregatePrices?.msrpListPriceLatestStock;
        break;
      default:
        console.log("Invalid price type");
    }
    return selectedPrice;
  };
  const columns: GridColDef[] = [
    {
      field: "modelName",
      headerName: "Model Name",
      width: 210,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography sx={{ cursor: "pointer" }} color="primary">
            {params.row.modelName}
          </Typography>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      width: 250,
      valueGetter: (params: GridValueGetterParams<any>) => params.row.title,
    },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      width: 150,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.manufacturer,
    },
    {
      field: "model",
      headerName: "Model",
      width: 250,
      valueGetter: (params: GridValueGetterParams<any>) => params.row.model,
    },
    {
      field: "price",
      headerName: "New Price ($)",
      headerAlign: "right",
      width: 150,
      filterable: false,
      type: "number",
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        handleGetSelectedPrice(params.row),
    },
  ];
  return (
    <>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "2px",
            paddingBottom: "2px",
          }}
        >
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Price Rule
          </Typography>
          <IconButton
            onClick={() => {
              handleClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ width: "auto" }}>
          <Paper
            elevation={3}
            sx={{ marginTop: 1, padding: "15px", borderRadius: "5px" }}
          >
            <Typography fontSize="small">
              Select any price from the followings to apply.
            </Typography>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={priceType}
                onChange={handlePriceTypeChange}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Grid container>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.Price.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          List Price{" "}
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                This is mfr list price.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                      sx={{ fontSize: "0.9rem" }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.UnitListPriceLargestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          Unit List Price Highest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the HIGHEST
                                price.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.UnitListPriceSmallestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          Unit List Price Lowest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the LOWEST
                                price.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.UnitListPriceLatestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          Unit List Price Latest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the LATEST
                                price.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.MSRPListPriceLargestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          MSRP List Price Highest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the HIGHEST
                                MSRP.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.MSRPListPriceSmallestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          MSRP List Price Lowest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the LOWEST
                                MSRP.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      value={PricingType.MSRPListPriceLatestStock.toString()}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          MSRP List Price Latest
                          <Tooltip
                            title={
                              <Typography fontSize={"14px"}>
                                It will look at all the units of that particular
                                model (Sold and Unsold) and grab the LATEST
                                MSRP.
                              </Typography>
                            }
                            placement="top"
                          >
                            <IconButton>
                              <InfoIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </RadioGroup>
            </FormControl>
          </Paper>
          <Box mt={1}>
            <Alert severity="info">
              The Products with zero stock will have List Price by default.
            </Alert>
          </Box>
          <Box mt={1}>
            <ServerAppGrid
              data={productsMutationResult.isLoading ? [] : products}
              columns={columns}
              isLoading={productsMutationResult.isLoading}
              paging={paging}
              setPaging={setPaging}
              totalRecords={productsCount}
              keepNonExistentRowsSelected={false}
              height={500}
              hideCheckBoxes={true}
            ></ServerAppGrid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            autoFocus
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setApplyRuleConfirm(true);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        open={applyRuleConfirm}
        onClose={handleCloseConfirmation}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Confirm to Apply
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure to apply this price to all products?
          </Typography>

          <Typography fontSize="small">
            Note: It can take 2 to 3 hours to change prices on shopify.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            autoFocus
            onClick={() => {
              setApplyRuleConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={setPriceSyncingMutationResult.isLoading}
            onClick={handleApplyPriceRule}
          >
            {setPriceSyncingMutationResult.isLoading ? (
              <CircularProgress size="1.2rem" color="inherit" />
            ) : (
              "Yes"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GridWithNewPrices;
